import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';

import { router } from './router';
import { VersionDisplay, VersionConsole } from './components/VersionDisplay';
import { FilaRefreshProvider } from './context/fila-refresh-context';

import '../../../global.css';

import './index.css';

// TypeScript declarations for Vite build-time constants
declare const __DEBUG_MODE__: boolean;
declare const __APP_VERSION__: any;

// Create a client with enhanced error handling for debug mode
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: __DEBUG_MODE__ ? 1 : false,
      refetchOnWindowFocus: false,
      staleTime: __DEBUG_MODE__ ? 1000 * 60 * 5 : 1000 * 60 * 10, // 5min debug, 10min prod
    },
    mutations: {
      retry: __DEBUG_MODE__ ? 1 : false,
    },
  },
});

// Enhanced error boundary for debug mode
const ErrorBoundary = __DEBUG_MODE__ ? ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      {children}
      <script
        dangerouslySetInnerHTML={{
          __html: `
            window.addEventListener('error', function(e) {
              console.error('🐛 Global error caught:', e.error);
              console.error('🐛 Error details:', {
                message: e.message,
                filename: e.filename,
                lineno: e.lineno,
                colno: e.colno,
                stack: e.error?.stack
              });
            });

            window.addEventListener('unhandledrejection', function(e) {
              console.error('🐛 Unhandled promise rejection:', e.reason);
              e.preventDefault();
            });

            // Enhanced React error reporting
            if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
              const originalError = console.error;
              console.error = function(...args) {
                originalError.apply(console, args);
                if (args[0] && typeof args[0] === 'string' && args[0].includes('Minified React error')) {
                  console.warn('🐛 React error detected. Check the full error message at: https://react.dev/errors/');
                }
              };
            }
          `,
        }}
      />
    </>
  );
} : ({ children }: { children: React.ReactNode }) => <>{children}</>;

const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);

  console.log('🚀 Starting Cantina Pay Fila application...');
  console.log(`📦 Version: ${__APP_VERSION__?.version || 'unknown'}`);
  console.log(`🐛 Debug Mode: ${__DEBUG_MODE__ ? 'ENABLED' : 'DISABLED'}`);
  console.log(`🌐 Environment: ${import.meta.env.MODE}`);

  root.render(
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <FilaRefreshProvider>
            <RouterProvider router={router} />
            <Toaster position="top-right" richColors />
          </FilaRefreshProvider>

          {/* Always include version console for logging */}
          <VersionConsole />

          {/* Version display component - Disabled */}
          {/* <VersionDisplay /> */}
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>,
  );
}
